import { motion } from 'framer-motion';
import { Briefcase, CalendarDays, FolderKanban, Megaphone, Target, Users } from 'lucide-react';
import { LANDING_SECTION_IDS } from './constants';

const PRODUCTS = [
  {
    icon: Users,
    title: 'Community & Members',
    description: 'Onboard members, manage roles, and keep your community organized from day one.',
    bullets: ['Member requests & approvals', 'Admin roles & permissions', 'Onboarding flows'],
  },
  {
    icon: Megaphone,
    title: 'Posts & Engagement',
    description: 'Share announcements, updates, and stories that keep members active and informed.',
    bullets: ['Community feed & posts', 'Announcements & updates', 'Member interactions'],
  },
  {
    icon: Target,
    title: 'Lead Capture',
    description: 'Turn community activity into business opportunities with structured lead posts.',
    bullets: ['Member-submitted leads', 'Lead visibility for admins', 'Pipeline-style tracking'],
  },
  {
    icon: CalendarDays,
    title: 'Events',
    description: 'Plan events, manage registrations, and check in attendees on the day.',
    bullets: ['Event creation & publishing', 'Registration management', 'QR scanning & check-in'],
  },
  {
    icon: Briefcase,
    title: 'Business Directory',
    description: 'Showcase member businesses with a directory that can be public or members-only.',
    bullets: ['Business listings & profiles', 'Public or private directory', 'Category filtering'],
  },
  {
    icon: FolderKanban,
    title: 'Projects',
    description: 'Highlight initiatives, collaborations, and opportunities inside your community.',
    bullets: ['Project showcases', 'Structured project pages', 'Community visibility'],
  },
] as const;

export function ProductSection() {
  return (
    <section id={LANDING_SECTION_IDS.product} className="py-24 bg-muted/30 scroll-mt-24">
      <div className="container px-6 mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">What Milora does</h2>
          <p className="text-muted-foreground text-lg">
            Everything community owners need to run, engage, and grow — in one platform.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {PRODUCTS.map((product, index) => (
            <motion.div
              key={product.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              whileHover={{ y: -4 }}
              className="glass-card p-8 rounded-2xl border border-border/50 shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl flex items-center justify-center mb-6 text-primary">
                <product.icon className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-3">{product.title}</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">{product.description}</p>
              <ul className="space-y-2">
                {product.bullets.map((bullet) => (
                  <li key={bullet} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    {bullet}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
